/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// @override — http(s) / protocol-relative hrefs must not go through buildUrl
// (that concatenates locale + origin onto an already-absolute URL).

import {useCallback} from 'react';
import {useHistory} from 'react-router';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils';
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url';

const NATIVE_SCHEME = /^(mailto:|tel:)/i;

/**
 * A convenience hook for programmatic navigation uses history's `push` or `replace`. The proper locale
 * is automatically prepended to the provided path. Additional args are passed through to `history`.
 * @returns {function} - Returns a navigate function that passes args to history methods.
 */
const useNavigation = () => {
  const history = useHistory();

  const {site, locale: localeShortCode, buildUrl} = useMultiSite();

  return useCallback(

    /**
     *
     * @param {string} path - path to navigate to
     * @param {('push'|'replace')} action - which history method to use
     * @param  {...any} args - additional args passed to `.push` or `.replace`
     */
    (path, action = 'push', ...args) => {
      let next = path;

      if (typeof path === 'string') {
        const trimmed = path.trim();

        if (NATIVE_SCHEME.test(trimmed)) {
          window.location.assign(trimmed);

          return;
        }

        if (isAbsoluteURL(trimmed)) {
          try {
            const url = new URL(trimmed, window.location.origin);

            if (url.origin !== window.location.origin) {
              window.location.assign(url.href);

              return;
            }

            next = `${url.pathname}${url.search}${url.hash}` || '/';
          } catch {
            window.location.assign(trimmed);

            return;
          }
        } else {
          next = trimmed;
        }
      }

      const updatedHref = buildUrl(removeSiteLocaleFromPath(next));

      history[action](next === '/' ? '/' : updatedHref, ...args);
    },
    [localeShortCode, site]
  );
};

export default useNavigation;
